import { Check, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ExternalLink, Heart, ImageOff, Loader2, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import type { Photo } from '../lib/api';
import { galleryStoryStyleOf, type GalleryStyle } from '../lib/galleryCustomisation';
import { handleMediaError, handleMediaLoad, type MediaErrorHandler } from '../lib/mediaRecovery';
import { formatBytes } from '../lib/utils';
import { Button } from './ui/Button';

export function GalleryGrid({
  photos,
  onDelete,
  canDelete = false,
  showPhotoDetails = true,
  variant = 'cards',
  selectedPhotoIds,
  onToggleSelected,
  favouritePhotoIds,
  onToggleFavourite,
  favouritesLocked = false,
  favouritesLimitReached = false,
  deletingPhotoId,
  galleryStyle = 'mosaic',
  onMediaError
}: {
  photos: Photo[];
  onDelete?: (photoId: string) => void;
  canDelete?: boolean;
  showPhotoDetails?: boolean;
  variant?: 'cards' | 'client';
  selectedPhotoIds?: Set<string>;
  onToggleSelected?: (photoId: string) => void;
  favouritePhotoIds?: Set<string>;
  onToggleFavourite?: (photoId: string) => void;
  favouritesLocked?: boolean;
  favouritesLimitReached?: boolean;
  deletingPhotoId?: string | null;
  galleryStyle?: GalleryStyle;
  onMediaError?: MediaErrorHandler;
}) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [direction, setDirection] = useState(0);
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);
  const preloadCache = useRef(new Map<string, Promise<void>>());
  const loadedUrls = useRef(new Set<string>());
  const navigationRequest = useRef(0);
  const activePhoto = activeIndex === null ? null : (photos[activeIndex] ?? null);

  function preloadPhoto(url: string) {
    if (loadedUrls.current.has(url)) {
      return Promise.resolve();
    }
    const existing = preloadCache.current.get(url);
    if (existing) {
      return existing;
    }
    const promise = new Promise<void>((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const decoded = typeof image.decode === 'function' ? image.decode().catch(() => undefined) : Promise.resolve();
        void decoded.then(() => {
          loadedUrls.current.add(url);
          resolve();
        });
      };
      image.onerror = () => reject(new Error('Could not load the preview.'));
      image.src = url;
    });
    preloadCache.current.set(url, promise);
    void promise.catch(() => preloadCache.current.delete(url));
    return promise;
  }

  function requestLightboxIndex(index: number, nextDirection: -1 | 0 | 1) {
    const target = photos[index];
    if (!target) {
      return;
    }
    const request = ++navigationRequest.current;
    setDirection(nextDirection);
    if (!target.url) {
      setPendingIndex(null);
      setActiveIndex(index);
      return;
    }
    setPendingIndex(index);
    void preloadPhoto(target.url)
      .then(() => {
        if (navigationRequest.current !== request) {
          return;
        }
        setActiveIndex(index);
        setPendingIndex(null);
      })
      .catch(() => {
        if (navigationRequest.current !== request) {
          return;
        }
        setPendingIndex(null);
        onMediaError?.();
      });
  }

  useEffect(() => {
    if (activeIndex === null) {
      return;
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setActiveIndex(null);
        return;
      }
      if (photos.length <= 1) {
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        moveLightbox(-1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        moveLightbox(1);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, photos.length]);

  useEffect(() => {
    if (activeIndex !== null && activeIndex >= photos.length) {
      setActiveIndex(photos.length > 0 ? photos.length - 1 : null);
    }
  }, [activeIndex, photos.length]);

  useEffect(() => {
    if (activeIndex === null) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [activeIndex]);

  function openLightbox(index: number) {
    navigationRequest.current += 1;
    setPendingIndex(null);
    setDirection(0);
    setActiveIndex(index);
  }

  function closeLightbox() {
    navigationRequest.current += 1;
    setPendingIndex(null);
    setActiveIndex(null);
  }

  function moveLightbox(delta: -1 | 1) {
    if (photos.length <= 1) {
      return;
    }
    const current = pendingIndex ?? activeIndex;
    if (current === null) {
      return;
    }
    requestLightboxIndex((current + delta + photos.length) % photos.length, delta);
  }

  useEffect(() => {
    if (activeIndex === null || photos.length <= 1) {
      return;
    }
    const adjacentIndexes = [
      (activeIndex - 1 + photos.length) % photos.length,
      (activeIndex + 1) % photos.length
    ];
    adjacentIndexes.forEach((index) => {
      const url = photos[index]?.url;
      if (url) {
        void preloadPhoto(url);
      }
    });
  }, [activeIndex, photos]);

  const hasCardActions = Boolean(canDelete && onDelete);
  const showCardFooter = showPhotoDetails || hasCardActions;
  const selectable = Boolean(selectedPhotoIds && onToggleSelected);
  const storyStyle = galleryStoryStyleOf(galleryStyle);
  const usesClientMasonry = variant === 'client' && storyStyle === 'mosaic';
  const masonryRef = useRef<HTMLDivElement | null>(null);
  const clientColumnCount = useClientMasonryColumnCount(usesClientMasonry, masonryRef);
  const clientColumns = useMemo(
    () => (usesClientMasonry ? buildMasonryColumns(photos, clientColumnCount) : []),
    [clientColumnCount, photos, usesClientMasonry]
  );

  if (photos.length === 0) {
    return (
      <div className="flex min-h-64 flex-col items-center justify-center rounded-sm border border-dashed border-ink/20 bg-white/60 p-8 text-center">
        <ImageOff className="mb-3 h-8 w-8 text-ink/70" />
        <p className="font-medium text-ink">No photos yet</p>
        <p className="mt-1 max-w-sm text-sm text-ink/70">Uploads will appear here as soon as they are completed.</p>
      </div>
    );
  }

  return (
    <>
      {usesClientMasonry ? (
        <div ref={masonryRef} className="client-masonry-grid" style={{ gridTemplateColumns: `repeat(${clientColumnCount}, minmax(0, 1fr))` }}>
          {clientColumns.map((column, columnIndex) => (
            <div key={columnIndex} className="client-masonry-column">
              {column.map(({ photo, index }) => (
                <PhotoCard
                  key={photo.photoId}
                  photo={photo}
                  index={index}
                  variant={variant}
                  isSelected={selectedPhotoIds?.has(photo.photoId) ?? false}
                  selectable={selectable}
                  showCardFooter={showCardFooter}
                  showPhotoDetails={showPhotoDetails}
                  hasCardActions={hasCardActions}
                  canDelete={canDelete}
                  onToggleSelected={onToggleSelected}
                  onDelete={onDelete}
                  deletingPhotoId={deletingPhotoId}
                  onOpenLightbox={openLightbox}
                  isFavourite={favouritePhotoIds?.has(photo.photoId) ?? false}
                  favouritable={Boolean(onToggleFavourite)}
                  favouritesLocked={favouritesLocked}
                  favouritesLimitReached={favouritesLimitReached}
                  onToggleFavourite={onToggleFavourite}
                  onMediaError={onMediaError}
                />
              ))}
            </div>
          ))}
        </div>
      ) : variant === 'client' && storyStyle === 'filmstrip' ? (
        <FilmstripPhotoLayout
          photos={photos}
          renderPhoto={(photo, index) => (
            <PhotoCard
              photo={photo}
              index={index}
              variant={variant}
              isSelected={selectedPhotoIds?.has(photo.photoId) ?? false}
              selectable={selectable}
              showCardFooter={showCardFooter}
              showPhotoDetails={showPhotoDetails}
              hasCardActions={hasCardActions}
              canDelete={canDelete}
              onToggleSelected={onToggleSelected}
              onDelete={onDelete}
              deletingPhotoId={deletingPhotoId}
              onOpenLightbox={openLightbox}
              isFavourite={favouritePhotoIds?.has(photo.photoId) ?? false}
              favouritable={Boolean(onToggleFavourite)}
              favouritesLocked={favouritesLocked}
              favouritesLimitReached={favouritesLimitReached}
              onToggleFavourite={onToggleFavourite}
              onMediaError={onMediaError}
            />
          )}
        />
      ) : (
        <div className="image-grid grid gap-3">
          {photos.map((photo, index) => (
            <PhotoCard
              key={photo.photoId}
              photo={photo}
              index={index}
              variant={variant}
              isSelected={selectedPhotoIds?.has(photo.photoId) ?? false}
              selectable={selectable}
              showCardFooter={showCardFooter}
              showPhotoDetails={showPhotoDetails}
              hasCardActions={hasCardActions}
              canDelete={canDelete}
              onToggleSelected={onToggleSelected}
              onDelete={onDelete}
              deletingPhotoId={deletingPhotoId}
              onOpenLightbox={openLightbox}
              isFavourite={favouritePhotoIds?.has(photo.photoId) ?? false}
              favouritable={Boolean(onToggleFavourite)}
              favouritesLocked={favouritesLocked}
              favouritesLimitReached={favouritesLimitReached}
              onToggleFavourite={onToggleFavourite}
              onMediaError={onMediaError}
            />
          ))}
        </div>
      )}

      {activePhoto ? createPortal((
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-ink p-3" role="dialog" aria-modal="true">
          {activePhoto.url ? (
            <img
              src={activePhoto.url}
              alt=""
              className="absolute inset-0 h-full w-full scale-110 object-cover opacity-35 blur-2xl"
              aria-hidden="true"
              onLoad={handleMediaLoad}
              onError={(event) => handleMediaError(event, onMediaError)}
            />
          ) : null}
          <div className="absolute inset-0 bg-ink/70 backdrop-blur-md" />
          <button className="absolute inset-0 cursor-default" aria-label="Close lightbox" onClick={closeLightbox} />
          <div className="relative z-10 flex max-h-full w-full max-w-7xl flex-col">
            <div className="mb-3 flex items-center justify-between gap-3 text-white">
              <div className="min-w-0">
                {showPhotoDetails ? <p className="truncate text-sm font-medium">{activePhoto.fileName}</p> : null}
                {photos.length > 1 && activeIndex !== null ? (
                  <p className="text-xs text-white/60">{activeIndex + 1} of {photos.length}</p>
                ) : null}
              </div>
              <div className="flex shrink-0 gap-2">
                {onToggleFavourite ? (
                  <Button
                    variant="secondary"
                    size="icon"
                    className="client-gallery-lightbox-heart"
                    data-selected={favouritePhotoIds?.has(activePhoto.photoId) ? 'true' : 'false'}
                    onClick={() => onToggleFavourite(activePhoto.photoId)}
                    disabled={favouritesLocked || (favouritesLimitReached && !favouritePhotoIds?.has(activePhoto.photoId))}
                    aria-label={`${favouritePhotoIds?.has(activePhoto.photoId) ? 'Remove' : 'Add'} ${activePhoto.fileName} ${favouritePhotoIds?.has(activePhoto.photoId) ? 'from' : 'to'} favourites`}
                  >
                    <Heart className="h-4 w-4" fill={favouritePhotoIds?.has(activePhoto.photoId) ? 'currentColor' : 'none'} />
                  </Button>
                ) : null}
                {activePhoto.url ? (
                  <Button asChild variant="secondary" size="sm">
                    <a href={activePhoto.url} target="_blank" rel="noreferrer">
                      <ExternalLink className="h-4 w-4" />
                      <span>Open preview</span>
                    </a>
                  </Button>
                ) : null}
                <Button variant="secondary" size="icon" onClick={closeLightbox} aria-label="Close lightbox">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="relative flex min-h-0 flex-1 items-center justify-center">
              {photos.length > 1 ? (
                <Button
                  variant="secondary"
                  size="icon"
                  className="absolute left-0 z-20 sm:left-2"
                  onClick={() => moveLightbox(-1)}
                  aria-label="Previous photo"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              ) : null}
              {activePhoto.url ? (
                <img
                  src={activePhoto.url}
                  alt={activePhoto.fileName}
                  className="lightbox-photo max-h-[calc(100vh-7rem)] max-w-full rounded-sm object-contain shadow-2xl ring-1 ring-white/10"
                  style={{ '--lightbox-offset': `${direction * 18}px` } as CSSProperties}
                  onLoad={handleMediaLoad}
                  onError={(event) => handleMediaError(event, onMediaError)}
                />
              ) : null}
              {photos.length > 1 ? (
                <Button
                  variant="secondary"
                  size="icon"
                  className="absolute right-0 z-20 sm:right-2"
                  onClick={() => moveLightbox(1)}
                  aria-label="Next photo"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      ), document.body) : null}
    </>
  );
}

function FilmstripPhotoLayout({
  photos,
  renderPhoto
}: {
  photos: Photo[];
  renderPhoto: (photo: Photo, index: number) => ReactNode;
}) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const animationFrameRef = useRef<number | null>(null);
  const snapTimerRef = useRef<number | null>(null);
  const layoutPhotoKeyRef = useRef<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const reducedMotion = usePrefersReducedMotion();
  const photoKey = useMemo(() => photos.map((photo) => photo.photoId).join('\u0000'), [photos]);

  const nearestItem = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport || photos.length === 0) {
      return { index: 0, left: 0, distance: 0 };
    }
    const viewportCenter = viewport.scrollLeft + viewport.clientWidth / 2;
    let nearest = { index: 0, left: 0, distance: Number.POSITIVE_INFINITY };
    itemRefs.current.forEach((item, index) => {
      if (!item) return;
      const itemCenter = item.offsetLeft + item.offsetWidth / 2;
      const distance = Math.abs(itemCenter - viewportCenter);
      if (distance < nearest.distance) {
        nearest = {
          index,
          left: itemCenter - viewport.clientWidth / 2,
          distance
        };
      }
    });
    return nearest;
  }, [photos.length]);

  const updatePresentation = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const viewportCenter = viewport.scrollLeft + viewport.clientWidth / 2;
    const spread = Math.max(viewport.clientWidth * 0.62, 1);
    const maximumDrop = Math.min(112, Math.max(42, viewport.clientHeight * 0.14));
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    itemRefs.current.forEach((item, index) => {
      if (!item) return;
      const itemCenter = item.offsetLeft + item.offsetWidth / 2;
      const centerDistance = itemCenter - viewportCenter;
      const absoluteCenterDistance = Math.abs(centerDistance);
      if (absoluteCenterDistance < nearestDistance) {
        nearestDistance = absoluteCenterDistance;
        nearestIndex = index;
      }
      const signedProgress = Math.max(-1, Math.min(1, centerDistance / spread));
      const absoluteProgress = Math.abs(signedProgress);
      const presentationKey = `${reducedMotion ? 'reduced' : 'motion'}:${maximumDrop.toFixed(1)}:${signedProgress.toFixed(3)}`;
      if (item.dataset.filmstripPresentation === presentationKey) return;
      item.dataset.filmstripPresentation = presentationKey;
      const drop = reducedMotion ? 0 : absoluteProgress * maximumDrop;
      const rotation = reducedMotion ? 0 : signedProgress * 4;
      const scale = reducedMotion ? 1 : 1 - absoluteProgress * 0.14;
      const opacity = reducedMotion ? 1 : 1 - absoluteProgress * 0.24;
      item.style.setProperty('--filmstrip-card-y', `${drop.toFixed(2)}px`);
      item.style.setProperty('--filmstrip-card-rotation', `${rotation.toFixed(2)}deg`);
      item.style.setProperty('--filmstrip-card-scale', scale.toFixed(3));
      item.style.setProperty('--filmstrip-card-opacity', opacity.toFixed(3));
    });

    setActiveIndex((current) => current === nearestIndex ? current : nearestIndex);
  }, [reducedMotion]);

  const schedulePresentationUpdate = useCallback(() => {
    if (animationFrameRef.current !== null) return;
    animationFrameRef.current = window.requestAnimationFrame(() => {
      animationFrameRef.current = null;
      updatePresentation();
    });
  }, [updatePresentation]);

  const scrollToIndex = useCallback((index: number, behavior: ScrollBehavior = 'smooth') => {
    const viewport = viewportRef.current;
    const item = itemRefs.current[index];
    if (!viewport || !item) return;
    const left = item.offsetLeft + item.offsetWidth / 2 - viewport.clientWidth / 2;
    viewport.scrollTo({
      left: Math.max(0, Math.min(left, viewport.scrollWidth - viewport.clientWidth)),
      behavior: reducedMotion ? 'auto' : behavior
    });
  }, [reducedMotion]);

  const settleNearestItem = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const nearest = nearestItem();
    if (nearest.distance <= 2) return;
    viewport.scrollTo({
      left: Math.max(0, Math.min(nearest.left, viewport.scrollWidth - viewport.clientWidth)),
      behavior: reducedMotion ? 'auto' : 'smooth'
    });
  }, [nearestItem, reducedMotion]);

  const scheduleSettle = useCallback(() => {
    if (snapTimerRef.current !== null) {
      window.clearTimeout(snapTimerRef.current);
    }
    snapTimerRef.current = window.setTimeout(() => {
      snapTimerRef.current = null;
      settleNearestItem();
    }, 140);
  }, [settleNearestItem]);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;

    itemRefs.current.length = photos.length;
    let resetToFirstItem = layoutPhotoKeyRef.current !== photoKey;
    layoutPhotoKeyRef.current = photoKey;
    const synchronizeGeometry = () => {
      const items = itemRefs.current.filter((item): item is HTMLDivElement => Boolean(item));
      const firstItem = items[0];
      const lastItem = items.at(-1);
      track.style.paddingLeft = `${Math.max(0, (viewport.clientWidth - (firstItem?.offsetWidth ?? 0)) / 2)}px`;
      track.style.paddingRight = `${Math.max(0, (viewport.clientWidth - (lastItem?.offsetWidth ?? 0)) / 2)}px`;
      if (resetToFirstItem) {
        resetToFirstItem = false;
        viewport.scrollLeft = 0;
        setActiveIndex(0);
      }
      schedulePresentationUpdate();
    };

    synchronizeGeometry();
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(synchronizeGeometry);
    resizeObserver?.observe(viewport);
    itemRefs.current.forEach((item) => {
      if (item) resizeObserver?.observe(item);
    });
    window.addEventListener('resize', synchronizeGeometry);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', synchronizeGeometry);
    };
  }, [photoKey, photos.length, schedulePresentationUpdate]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const handleHorizontalWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) || Math.abs(event.deltaX) < 1) {
        return;
      }
      const maximumScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const canAdvance = event.deltaX > 0
        ? viewport.scrollLeft < maximumScroll - 1
        : viewport.scrollLeft > 1;
      if (!canAdvance) return;
      const scale = event.deltaMode === WheelEvent.DOM_DELTA_LINE
        ? 16
        : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
          ? viewport.clientWidth
          : 1;
      event.preventDefault();
      viewport.scrollLeft = Math.max(0, Math.min(maximumScroll, viewport.scrollLeft + event.deltaX * scale));
      schedulePresentationUpdate();
      scheduleSettle();
    };
    viewport.addEventListener('wheel', handleHorizontalWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', handleHorizontalWheel);
  }, [schedulePresentationUpdate, scheduleSettle]);

  useEffect(() => () => {
    if (animationFrameRef.current !== null) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (snapTimerRef.current !== null) {
      window.clearTimeout(snapTimerRef.current);
      snapTimerRef.current = null;
    }
  }, []);

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.target !== event.currentTarget) return;
    const targetIndex = event.key === 'ArrowLeft'
      ? Math.max(0, activeIndex - 1)
      : event.key === 'ArrowRight'
        ? Math.min(photos.length - 1, activeIndex + 1)
        : event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? photos.length - 1
            : null;
    if (targetIndex === null) return;
    event.preventDefault();
    scrollToIndex(targetIndex, event.key === 'Home' || event.key === 'End' ? 'auto' : 'smooth');
  }

  return (
    <section className="client-gallery-filmstrip" aria-label="Filmstrip photos" data-filmstrip-gallery>
      <div
        ref={viewportRef}
        className="client-gallery-filmstrip-viewport"
        role="region"
        aria-roledescription="carousel"
        aria-label="Scrollable photo filmstrip. Use the arrow keys, horizontal trackpad gestures, or swipe to browse."
        tabIndex={0}
        onScroll={() => {
          schedulePresentationUpdate();
          scheduleSettle();
        }}
        onKeyDown={handleKeyDown}
        data-filmstrip-viewport
      >
        <div ref={trackRef} className="client-gallery-filmstrip-track" data-filmstrip-track data-filmstrip-preview="true">
          {photos.map((photo, index) => (
            <div
              key={photo.photoId}
              ref={(item) => { itemRefs.current[index] = item; }}
              className="client-gallery-filmstrip-item"
              style={{ '--filmstrip-aspect-ratio': photoAspectRatio(photo) } as CSSProperties}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${photos.length}`}
              data-filmstrip-item
              data-active={activeIndex === index ? 'true' : 'false'}
            >
              {renderPhoto(photo, index)}
            </div>
          ))}
        </div>
      </div>

      <div className="client-gallery-filmstrip-controls" aria-label="Filmstrip navigation">
        <button
          type="button"
          className="client-gallery-filmstrip-control"
          onClick={() => scrollToIndex(Math.max(0, activeIndex - 10))}
          disabled={activeIndex === 0}
          aria-label="Go back 10 photos"
        >
          <ChevronsLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          className="client-gallery-filmstrip-control"
          onClick={() => scrollToIndex(Math.max(0, activeIndex - 1))}
          disabled={activeIndex === 0}
          aria-label="Previous photo"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <p className="client-gallery-filmstrip-counter" aria-live="polite" aria-atomic="true">
          {activeIndex + 1} of {photos.length}
        </p>
        <button
          type="button"
          className="client-gallery-filmstrip-control"
          onClick={() => scrollToIndex(Math.min(photos.length - 1, activeIndex + 1))}
          disabled={activeIndex === photos.length - 1}
          aria-label="Next photo"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
        <button
          type="button"
          className="client-gallery-filmstrip-control"
          onClick={() => scrollToIndex(Math.min(photos.length - 1, activeIndex + 10))}
          disabled={activeIndex === photos.length - 1}
          aria-label="Skip ahead 10 photos"
        >
          <ChevronsRight className="h-5 w-5" />
        </button>
      </div>
    </section>
  );
}

function photoAspectRatio(photo: Photo) {
  const width = photo.previewWidth ?? photo.width;
  const height = photo.previewHeight ?? photo.height;
  return width && height ? `${width} / ${height}` : '4 / 3';
}

function usePrefersReducedMotion() {
  const [reducedMotion, setReducedMotion] = useState(() => (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  ));

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);

  return reducedMotion;
}

function PhotoCard({
  photo,
  index,
  variant,
  isSelected,
  selectable,
  showCardFooter,
  showPhotoDetails,
  hasCardActions,
  canDelete,
  onToggleSelected,
  onDelete,
  deletingPhotoId,
  onOpenLightbox,
  isFavourite,
  favouritable,
  favouritesLocked,
  favouritesLimitReached,
  onToggleFavourite,
  onMediaError
}: {
  photo: Photo;
  index: number;
  variant: 'cards' | 'client';
  isSelected: boolean;
  selectable: boolean;
  showCardFooter: boolean;
  showPhotoDetails: boolean;
  hasCardActions: boolean;
  canDelete: boolean;
  onToggleSelected?: (photoId: string) => void;
  onDelete?: (photoId: string) => void;
  deletingPhotoId?: string | null;
  onOpenLightbox: (index: number) => void;
  isFavourite: boolean;
  favouritable: boolean;
  favouritesLocked: boolean;
  favouritesLimitReached: boolean;
  onToggleFavourite?: (photoId: string) => void;
  onMediaError?: MediaErrorHandler;
}) {
  return (
    <article
      data-gallery-item-index={index}
      className={
        variant === 'client'
          ? 'group relative overflow-hidden bg-smoke'
          : `group relative overflow-hidden rounded-sm bg-white shadow-sm ring-1 ${isSelected ? 'ring-2 ring-sea' : 'ring-ink/10'}`
      }
    >
      {selectable ? (
        <button
          type="button"
          className={`absolute left-2 top-2 z-10 flex h-11 w-11 items-center justify-center rounded-sm shadow-sm ring-1 ring-ink/10 ${
            isSelected ? 'bg-sea text-white' : 'bg-white/95 text-ink/75 hover:bg-white'
          }`}
          onClick={() => onToggleSelected?.(photo.photoId)}
          aria-label={`${isSelected ? 'Deselect' : 'Select'} ${photo.fileName}`}
        >
          {isSelected ? <Check className="h-4 w-4" /> : <span className="h-4 w-4 rounded-sm border-2 border-current" aria-hidden="true" />}
        </button>
      ) : null}
      {favouritable ? (
        <button
          type="button"
          className="client-gallery-heart absolute right-2 top-2 z-10 flex h-11 w-11 items-center justify-center rounded-full shadow-sm ring-1 transition"
          data-selected={isFavourite ? 'true' : 'false'}
          onClick={() => onToggleFavourite?.(photo.photoId)}
          disabled={favouritesLocked || (favouritesLimitReached && !isFavourite)}
          aria-label={`${isFavourite ? 'Remove' : 'Add'} ${photo.fileName} ${isFavourite ? 'from' : 'to'} favourites`}
          title={favouritesLimitReached && !isFavourite ? 'Remove a favourite before adding another' : undefined}
        >
          <Heart className="h-5 w-5" fill={isFavourite ? 'currentColor' : 'none'} />
        </button>
      ) : null}
      <div className="relative">
        <button
          type="button"
          className={variant === 'client' ? 'client-gallery-photo-trigger block w-full bg-smoke text-left' : 'block aspect-[4/3] w-full bg-smoke text-left'}
          onClick={() => (selectable ? onToggleSelected?.(photo.photoId) : onOpenLightbox(index))}
          aria-label={selectable ? `${isSelected ? 'Deselect' : 'Select'} ${photo.fileName} card` : `Open ${photo.fileName}`}
        >
          {photo.url ? (
            <img
              src={photo.url}
              alt={photo.fileName}
              width={photo.previewWidth ?? photo.width}
              height={photo.previewHeight ?? photo.height}
              className={
                variant === 'client'
                  ? 'client-gallery-photo block h-auto w-full object-cover transition duration-300 group-hover:scale-[1.015]'
                  : 'h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]'
              }
              loading="lazy"
              onLoad={handleMediaLoad}
              onError={(event) => handleMediaError(event, onMediaError)}
            />
          ) : (
            <div className="flex aspect-[4/3] h-full items-center justify-center text-ink/70">
              <ImageOff className="h-8 w-8" />
            </div>
          )}
        </button>
        {showCardFooter ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-ink/88 via-ink/62 to-transparent p-3 text-white opacity-0 transition duration-200 group-hover:opacity-100 group-focus-within:opacity-100">
            <div className="pointer-events-auto flex min-h-16 items-end justify-between gap-3">
              {showPhotoDetails ? (
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{photo.fileName}</p>
                  <p className="text-xs text-white/80">
                    {formatBytes(photo.sizeBytes)}
                    {photo.scene ? ` · ${photo.scene}` : ' · No scene'}
                  </p>
                </div>
              ) : (
                <div className="min-w-0" />
              )}
              {hasCardActions ? (
                <div className="flex shrink-0 items-center gap-1">
                  {canDelete && onDelete ? (
                    <Button
                      variant="secondary"
                      size="icon"
                      onClick={(event) => {
                        event.stopPropagation();
                        onDelete(photo.photoId);
                      }}
                      disabled={deletingPhotoId === photo.photoId}
                      aria-label={deletingPhotoId === photo.photoId ? `Deleting ${photo.fileName}` : `Delete ${photo.fileName}`}
                    >
                      {deletingPhotoId === photo.photoId ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </article>
  );
}

function useClientMasonryColumnCount(enabled: boolean, containerRef: RefObject<HTMLDivElement | null>) {
  const [columnCount, setColumnCount] = useState(() => getClientMasonryColumnCount());

  useEffect(() => {
    if (!enabled) {
      return;
    }
    function updateColumnCount() {
      setColumnCount(getClientMasonryColumnCount(containerRef.current?.clientWidth));
    }
    updateColumnCount();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateColumnCount);
    if (containerRef.current) observer?.observe(containerRef.current);
    window.addEventListener('resize', updateColumnCount);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateColumnCount);
    };
  }, [containerRef, enabled]);

  return columnCount;
}

function getClientMasonryColumnCount(containerWidth?: number) {
  const width = containerWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 1440);
  if (width >= 1200) {
    return 4;
  }
  if (width >= 768) {
    return 3;
  }
  return 2;
}

function buildMasonryColumns(photos: Photo[], columnCount: number) {
  const columns = Array.from({ length: columnCount }, () => [] as Array<{ photo: Photo; index: number }>);
  const columnHeights = Array.from({ length: columnCount }, () => 0);

  photos.forEach((photo, index) => {
    const shortestColumnIndex = columnHeights.indexOf(Math.min(...columnHeights));
    columns[shortestColumnIndex].push({ photo, index });
    columnHeights[shortestColumnIndex] += photoAspectHeight(photo);
  });

  return columns;
}

function photoAspectHeight(photo: Photo) {
  const width = photo.previewWidth ?? photo.width;
  const height = photo.previewHeight ?? photo.height;
  return width && height ? height / width : 0.75;
}
